/**
 * BatchReportPage — Production Report module entry tại
 * `/factory/<code>/report/batch-report`.
 *
 * Spec: `frontend/plan/Excel_Production_Report_Web_UI_Conversion_Spec.md`.
 *
 * Module wrap 6 tab nội bộ tương ứng 6 sheet Excel:
 *   1. Dashboard            — executive KPI + 6 chart blocks (§8).
 *   2. Batch Report         — INPUT/OUTPUT grid (§3).
 *   3. Production Analysis  — budget/actual + loss + extracted value (§4).
 *   4. Quy chuẩn  — standardization grid (§5).
 *   5. Comparison           — current vs previous PO (§6).
 *   6. BOM-BTA1             — BOM template/config + costs (§7).
 *   +  Item                 — tab bổ sung (ngoài Excel), scaffold chờ spec.
 *
 * Shared state:
 * - `poNo`             — PO selector (sync across tabs, §1.3 batch context).
 * - `mode`             — `qui chuan` / `chua qui chuan` toggle (§4.3 D3).
 * - `showAdvanced`     — column expansion (Batch Report + BOM tabs).
 * - `activeTab`        — landing default = Dashboard (§1.2).
 *
 * Mock data hiện hardcode cho PO `PRO-TLBL-25097`. Khi BE expose
 * `/api/production-report/{po_no}/...` thì RTK Query thay block mock.
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { useDispatch } from 'react-redux'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker, useLocation, useNavigate } from 'react-router-dom'

import { apiSlice } from '@/store/api/apiSlice'
import { baseApi } from '@/store/api/baseApi'
import { useAppSelector } from '@/hooks/redux'
import { useGetFactoryTabRowsQuery } from '@/store/api/factoryApi'
import {
  useGetBatchReportPersistQuery,
  useGetBomQualityRefsQuery,
  useSetBatchReportStandardMutation,
  useSetBatchReportStatusMutation,
  useSetBatchReportCompareWithMutation,
} from '@/store/api/factoryBomApi'
import { usePreference } from '@/hooks/usePreference'
import { useIsMobile } from '@/hooks/useResponsive'
import { COMBOBOX_FILTER_COMPACT, HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'
import { useIsDesktop } from '@/hooks/useResponsive'
import PoFinishedDialog from '../baoLoc/PoFinishedDialog'
import { formatAccessDate } from './tabs/formatters'

import { downloadBatchReportExport } from './export'
import BatchReportTab from './tabs/BatchReportTab'
import BomTab, { type BomTabHandle } from './tabs/BomTab'
import ComparisonTab from './tabs/ComparisonTab'
import DashboardTab from './tabs/DashboardTab'
import ItemTab, { type ItemTabHandle } from './tabs/ItemTab'
import ProductionAnalysisTab from './tabs/ProductionAnalysisTab'
import StandardizationTab from './tabs/StandardizationTab'
import { COLORS } from './tabs/styleTokens'

type Mode = 'chua qui chuan' | 'qui chuan'

type TabKey =
  | 'dashboard'
  | 'batch-report'
  | 'production-analysis'
  | 'standardization'
  | 'comparison'
  | 'item'
  | 'bom'

interface TabConfig {
  key: TabKey
  label: string
}

// Tab label cho `bom` được derive dynamic theo selectedBomCode (BOM-<code>),
// fallback BTA1 khi PO chưa khớp / chưa chọn. Các tab khác hard-code.
const STATIC_TABS: TabConfig[] = [
  { key: 'production-analysis', label: 'Analysis' },
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'batch-report', label: 'Batch Report' },
  { key: 'standardization', label: 'Standardization' },
  { key: 'comparison', label: 'Comparison' },
  { key: 'item', label: 'Other info' },
]

export interface BatchReportPageProps {
  factoryCode: string
  /** Date range của Filter chung (factory hub) — PO List load trong khoảng này. */
  startDate: string
  endDate: string
  /** Permission gating — truyền xuống BomTab (nút Save report). */
  canEdit?: boolean
  /** Counter từ nút Reload chung (FactoryHeader) — tăng mỗi lần bấm để arm lại
   *  overlay Loading như khi đổi tab (ngoài invalidate cache đã làm ở parent). */
  reloadSignal?: number
  onBackToHub?: () => void
}

const TAB_KEYS: readonly TabKey[] = [
  'dashboard',
  'batch-report',
  'production-analysis',
  'standardization',
  'comparison',
  'item',
  'bom',
] as const
const DEFAULT_TAB: TabKey = 'production-analysis'

function isTabKey(v: string | undefined): v is TabKey {
  return !!v && (TAB_KEYS as readonly string[]).includes(v)
}

// Endpoint (RTK Query) cấp DỮ LIỆU CHÍNH của từng tab — overlay Loading chờ
// đúng query này trả về rồi mới bỏ (chỉ render UI tab khi API xong). Đổi PO /
// đổi tab → các query refetch → pending → overlay tới khi settle.
const CONTENT_ENDPOINTS_BY_TAB: Record<TabKey, string[]> = {
  dashboard: ['getBatchReportDashboard'],
  'batch-report': ['getBatchReportInput', 'getBatchReportOutput'],
  'production-analysis': ['getProductionAnalysis'],
  standardization: ['getStandardizationRows'],
  comparison: ['getBatchReportComparison'],
  item: ['getBatchReportItemInfo', 'getQualityStandardList'],
  bom: ['getFactoryBomDetail', 'getBomQualityRefs', 'getFactoryCostList', 'getBomCropOptions', 'getFactoryBomOptions', 'getBagMix'],
}

type RtkQueryEntry = { status?: string; endpointName?: string } | undefined
type RtkSlice = { queries?: Record<string, RtkQueryEntry> }

function sliceHasPendingEndpoint(slice: RtkSlice | undefined, endpoints: Set<string>): boolean {
  if (!slice?.queries || endpoints.size === 0) return false
  for (const entry of Object.values(slice.queries)) {
    if (entry?.status !== 'pending') continue
    if (entry.endpointName && endpoints.has(entry.endpointName)) return true
  }
  return false
}

export default function BatchReportPage({ factoryCode, startDate, endDate, canEdit = false, reloadSignal = 0, onBackToHub }: BatchReportPageProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  // URL segment 3 (sau /<code>/report/batch-report/<tabSlug>) là active tab.
  // Empty/invalid → fallback `dashboard`. Mỗi click tab → navigate URL mới
  // (browser back/forward + deep-link share work out-of-box).
  // URL pattern: segments[0]=factoryCode, [1]='report', [2]='batch-report', [3]=inner tab.
  const activeTab: TabKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    return isTabKey(segments[3]) ? segments[3] : DEFAULT_TAB
  }, [location.pathname])

  // ----- Unsaved-changes guard (BOM + Item report) ------------------------
  // BomTab/ItemTab báo dirty qua onDirtyChange. useBlocker (data router) chặn MỌI
  // đổi URL rời tab đang edit khi dirty — gồm đổi report tab, factory tab,
  // sidebar, back trình duyệt. Đổi PO là state (không đổi URL) nên guard riêng
  // qua guardedRun. beforeunload lo đóng/refresh trình duyệt. Chỉ 1 tab mount tại
  // 1 thời điểm nên tối đa 1 ref non-null.
  const bomRef = useRef<BomTabHandle>(null)
  const itemRef = useRef<ItemTabHandle>(null)
  const [bomDirty, setBomDirty] = useState(false)
  const [itemDirty, setItemDirty] = useState(false)
  const anyDirty = bomDirty || itemDirty
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null)
  const [savingNav, setSavingNav] = useState(false)
  const guardedRun = (proceed: () => void) => {
    if (anyDirty) setPendingNav(() => proceed)
    else proceed()
  }
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      anyDirty && currentLocation.pathname !== nextLocation.pathname,
  )
  // Cảnh báo khi đóng/refresh tab trình duyệt (native prompt — không có nút Save).
  useEffect(() => {
    if (!anyDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [anyDirty])

  // Per-user pref (user_preferences scope='factory' key='batch_report', nested theo
  // factory_code): `poNo` = PO chọn cuối, `tab` = tab nội bộ dùng cuối. [[project_user_preferences_unification]]
  const poPref = usePreference<{ poNo: string; tab?: TabKey }>('factory', 'batch_report', factoryCode)

  const setActiveTab = (key: TabKey) => {
    navigate(`/${factoryCode}/report/batch-report/${key}`)
    void poPref.patch({ tab: key }) // log tab cuối user dùng → lần sau vào load lại
  }

  // Dialog mở khi: route bị block (useBlocker) HOẶC đổi PO khi dirty.
  const guardOpen = blocker.state === 'blocked' || pendingNav != null
  const resolveLeave = () => {
    if (blocker.state === 'blocked') blocker.proceed()
    else {
      const fn = pendingNav
      setPendingNav(null)
      fn?.()
    }
  }
  const cancelLeave = () => {
    if (blocker.state === 'blocked') blocker.reset()
    else setPendingNav(null)
  }
  const saveThenLeave = async () => {
    setSavingNav(true)
    // Chỉ 1 tab mount → ref còn lại null (skip). Save tab đang edit, ok = true nếu
    // không có tab dirty nào lỗi.
    const okBom = bomRef.current ? await bomRef.current.saveAll() : true
    const okItem = itemRef.current ? await itemRef.current.saveAll() : true
    setSavingNav(false)
    if (okBom && okItem) resolveLeave()
  }

  const [poNo, setPoNo] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  // Comparison PO (controlled — ComparisonTab dùng + Export đính kèm để sheet
  // Comparison so đúng PO web đang chọn). Default = PO đầu khác current.
  const [comparisonPo, setComparisonPo] = useState('')
  // Hydrate "Compare with" 1 lần / PO khi mở page (đọc persist.compareWith).
  const cmpHydratedFor = useRef('')

  // Reload toàn report: invalidate cache RTK Query của module → mọi tab refetch
  // live data (po-list, BOM detail/options/crop, cost, qrefs, batch-report persist).
  const dispatch = useDispatch()
  // Reload toàn bộ data bên dưới: invalidate broad → mọi tab refetch live.
  // Dùng nội bộ sau khi đổi status/standard; nút Reload đã gộp vào nút Reload
  // chung của Factory (FactoryHeader → handleReload).
  const reloadData = () => {
    dispatch(baseApi.util.invalidateTags(['Factory', 'FactoryBom', 'FactoryCost']))
  }
  // Bump để remount RIÊNG vùng content (tab active) — dùng sau khi đổi status:
  // dựng lại sạch từ data tươi mà không reload cả web/app-shell. Cũng feed vào
  // arm-effect để bật overlay Loading (mượt như Reload chung).
  const [statusReloadSignal, setStatusReloadSignal] = useState(0)

  // Export PO Report → multi-sheet .xlsx (BE generate). Cần factory + PO.
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const handleExport = async () => {
    if (!factoryCode || !poNo.trim() || exporting) return
    setExporting(true)
    try {
      await downloadBatchReportExport(factoryCode, poNo.trim(), comparisonPo)
    } catch {
      setExportError('Export failed. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  // Đổi tab nội bộ (URL đã commit) → reload live data cho tab mới (như nút Reload).
  // Bỏ qua lần đầu (query tự fetch khi mount). useBlocker chặn rời tab `bom` khi
  // dirty nên invalidate chỉ chạy SAU khi đã Save/Discard → không reset edit dở.
  const tabLoadedRef = useRef<TabKey | null>(null)
  useEffect(() => {
    if (tabLoadedRef.current === activeTab) return
    const first = tabLoadedRef.current === null
    tabLoadedRef.current = activeTab
    if (!first) dispatch(baseApi.util.invalidateTags(['Factory', 'FactoryBom', 'FactoryCost']))
  }, [activeTab, dispatch])

  // ----- Loading gate cho vùng content -----------------------------------
  // Đổi PO hoặc đổi tab → arm overlay Loading; bỏ khi query data của tab active
  // đã settle (hoặc cached → bỏ sau 1 nhịp ngắn). Chỉ áp khi đã chọn PO.
  const contentEndpoints = useMemo(
    () => new Set(CONTENT_ENDPOINTS_BY_TAB[activeTab] ?? []),
    [activeTab],
  )
  const contentFetching = useAppSelector((s) => {
    const state = s as unknown as Record<string, unknown>
    return (
      sliceHasPendingEndpoint(state[apiSlice.reducerPath] as RtkSlice | undefined, contentEndpoints) ||
      sliceHasPendingEndpoint(state[baseApi.reducerPath] as RtkSlice | undefined, contentEndpoints)
    )
  })
  const contentKey = `${activeTab}|${poNo.trim()}`
  const [showContentLoading, setShowContentLoading] = useState(false)
  // `armed` = vừa arm nhưng CHƯA thấy query nào pending; `fetching` = đã thấy
  // refetch chạy. Tách 2 pha để Reload không bị tắt overlay trước khi refetch kịp
  // set pending (đua 80ms cũ → cần bấm 2 lần). Ref (không trigger render).
  const settlePhaseRef = useRef<'armed' | 'fetching'>('armed')
  // Arm: mỗi lần đổi (tab|PO) HOẶC bấm Reload chung (reloadSignal) → bật Loading
  // ngay (nếu đã có PO). Reload ở parent đã invalidate cache → query refetch.
  useEffect(() => {
    settlePhaseRef.current = 'armed'
    setShowContentLoading(!!poNo.trim())
  }, [contentKey, poNo, reloadSignal, statusReloadSignal])
  // Disarm:
  //  - Đang pending → đánh dấu đã 'fetching', giữ overlay.
  //  - Hết pending mà CHƯA từng thấy fetch (pha 'armed') → đợi tới 400ms cho
  //    refetch (do invalidate) kịp khởi động; quá hạn coi như cache/no-op → tắt.
  //  - Hết pending sau khi đã fetch → settle nhanh 80ms.
  useEffect(() => {
    if (!showContentLoading) return
    if (contentFetching) {
      settlePhaseRef.current = 'fetching'
      return
    }
    const wait = settlePhaseRef.current === 'armed' ? 400 : 80
    const id = window.setTimeout(() => setShowContentLoading(false), wait)
    return () => window.clearTimeout(id)
  }, [contentFetching, showContentLoading])

  // PO List combo — load từ factory tab `po-list` trong khoảng Date from–to của
  // Filter chung (factory hub, prop `startDate`/`endDate`). BE `_list_po` filter
  // `po_date BETWEEN start AND end`.
  const { data: poListRes, refetch: refetchPoList } = useGetFactoryTabRowsQuery(
    {
      factoryCode,
      tab: 'po-list',
      startDate,
      endDate,
    },
    { skip: !factoryCode || !startDate || !endDate },
  )

  // Mỗi option PO kèm BOM Code + Date PO để hiện trong dropdown. Value vẫn là
  // chuỗi po_no (giữ nguyên state `poNo` + luồng derive bom_code phía dưới);
  // metadata tra cứu qua `poMeta` khi render option.
  const { poNoOptions, poMeta } = useMemo(() => {
    const items = poListRes?.items ?? []
    const seen = new Set<string>()
    const options: string[] = []
    const meta: Record<string, { bomCode: string; poDate: string; isFinished: boolean }> = {}
    for (const row of items) {
      const v = row['po_no']
      const s = v == null ? '' : String(v).trim()
      if (!s || seen.has(s)) continue
      seen.add(s)
      options.push(s)
      meta[s] = {
        bomCode: String(row['bom_code'] ?? row['bomCode'] ?? '').trim(),
        poDate: formatAccessDate(row['po_date']),
        // is_finished (view_factory_po1): true = Finished, false = Processing.
        isFinished: row['is_finished'] === true || row['is_finished'] === 1 || row['is_finished'] === '1',
      }
    }
    return { poNoOptions: options, poMeta: meta }
  }, [poListRes])

  // Chọn PO (user action) + persist delta cho user. CHỈ ghi pref khi user thật
  // sự chọn (onChange) — auto-pick/normalize bên dưới KHÔNG ghi.
  const selectPo = (next: string) => {
    guardedRun(() => {
      setPoNo(next)
      if (next) void poPref.patch({ poNo: next })
    })
  }

  // PO chọn LUÔN nằm trong danh sách đã lọc theo Date filter chung. Effect đảm
  // bảo invariant đó: nếu poNo rỗng/ngoài range → (re)pick = PO đã lưu nếu còn
  // trong range, else PO đầu danh sách. KHÔNG default mock `PRO-TLBL-25097`.
  // KHÔNG patch ở đây (auto-pick không phải lựa chọn của user → không log).
  useEffect(() => {
    if (poPref.isLoading) return // chờ pref load xong mới quyết định
    if (poNoOptions.length === 0) return // range không có PO → để nguyên
    if (poNo && poNoOptions.includes(poNo)) return // selection còn hợp lệ
    const saved = poPref.value.poNo?.trim()
    setPoNo(saved && poNoOptions.includes(saved) ? saved : poNoOptions[0])
  }, [poNo, poNoOptions, poPref.isLoading, poPref.value.poNo])

  // Mỗi PO List row có cột `bom_code` (Access form PoList ComboBox `public_v_factory_bom`).
  // BOM-BTA1 tab load BOM theo PO No đang chọn → tra cứu row khớp po_no rồi
  // pull bom_code. Empty string khi chưa match → BomTab fallback giá trị mock.
  // - So sánh po_no case-insensitive + trim để cover input gõ tay khác case.
  // - Defensive đọc bom_code lẫn camelCase bomCode trong trường hợp BE response
  //   serializer khác convention (current `_row()` giữ snake_case nhưng đề
  //   phòng tương lai).
  const selectedBomCode = useMemo(() => {
    const items = poListRes?.items ?? []
    const needle = poNo.trim().toUpperCase()
    if (!needle) return ''
    const row = items.find(
      (r) => String(r['po_no'] ?? '').trim().toUpperCase() === needle,
    )
    if (!row) return ''
    const raw = (row['bom_code'] ?? row['bomCode'] ?? '') as string | number | boolean
    return String(raw).trim()
  }, [poListRes, poNo])

  // PO date (ISO YYYY-MM-DD) của PO đang chọn → BomTab filter LIFFE/DIFF theo
  // ngày PO (v_changecost_quality). Empty khi chưa match.
  const selectedPoDateISO = useMemo(() => {
    const items = poListRes?.items ?? []
    const needle = poNo.trim().toUpperCase()
    if (!needle) return ''
    const row = items.find((r) => String(r['po_no'] ?? '').trim().toUpperCase() === needle)
    const raw = row?.['po_date']
    if (raw == null || raw === '') return ''
    const d = new Date(String(raw))
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
  }, [poListRes, poNo])

  // Ngày PO của PO đang chọn (cùng row khớp po_no) → BomTab dùng làm "Last revised".
  const selectedPoDate = useMemo(() => {
    const items = poListRes?.items ?? []
    const needle = poNo.trim().toUpperCase()
    if (!needle) return ''
    const row = items.find((r) => String(r['po_no'] ?? '').trim().toUpperCase() === needle)
    return row ? formatAccessDate(row['po_date']) : ''
  }, [poListRes, poNo])

  // Persist (bom_edit/cost_edit/is_finished) của PO đang chọn — BomTab tự query
  // lại cùng key (RTK dedupe) để hydrate + Save; trang dùng cho status/standard.
  const {
    data: persist,
    isLoading: persistLoading,
    refetch: refetchPersist,
  } = useGetBatchReportPersistQuery(
    { factoryCode, poNo: poNo.trim() },
    { skip: !factoryCode || !poNo.trim() },
  )

  // Comparison PO: mở page → hydrate từ persist.compareWith (đã lưu trong
  // bom_edit.compare_with); chưa có/không hợp lệ → default PO đầu KHÁC current.
  // Sau khi hydrate cho 1 PO rồi thì chỉ hội tụ khi value không còn hợp lệ.
  useEffect(() => {
    if (!poNo || !poNoOptions.length) return // chờ PO + options load
    const valid = (v: string) => !!v && poNoOptions.includes(v) && v !== poNo
    if (cmpHydratedFor.current !== poNo) {
      if (persistLoading) return // chờ persist load để đọc đúng value đã lưu
      cmpHydratedFor.current = poNo
      const saved = (persist?.compareWith ?? '').trim()
      setComparisonPo(valid(saved) ? saved : (poNoOptions.find((p) => p !== poNo) ?? ''))
      return
    }
    if (valid(comparisonPo)) return
    setComparisonPo(poNoOptions.find((p) => p !== poNo) ?? '')
  }, [poNo, poNoOptions, comparisonPo, persist, persistLoading])

  // User đổi "Compare with" → set state + persist vào bom_edit.compare_with.
  const [setCompareWith] = useSetBatchReportCompareWithMutation()
  const handleComparisonPoChange = (v: string) => {
    setComparisonPo(v)
    if (poNo.trim()) {
      setCompareWith({ factoryCode, poNo: poNo.trim(), compareWith: v }).unwrap().catch(() => undefined)
    }
  }

  // Batch Report header (Excel row1-3) Input Value = LIFFE + DIFF Ro (Robusta
  // R13-ST1). Dùng CHUNG query qrefs với BOM tab (cùng args factoryCode +
  // poDateISO → RTK Query dedupe = 1 API call, không gọi nhiều endpoint khác).
  const { data: qrefs } = useGetBomQualityRefsQuery(
    { factoryCode, date: selectedPoDateISO || undefined },
    { skip: !factoryCode },
  )
  const batchInputValue = useMemo(() => {
    const ro = qrefs?.items.find((i) => i.code === 'R13-ST1')
    if (ro?.liffe == null) return null
    return ro.liffe + (ro.diff ?? 0)
  }, [qrefs])

  // Khôi phục TAB cuối user dùng khi VÀO module — 1 lần, sau khi pref load. (Bỏ rule
  // cũ Finished→Analysis / Processing→BOM.) Tab đã lưu được ưu tiên kể cả khi URL trỏ
  // tab hợp lệ khác (giống hành vi replace cũ). Không có pref → DEFAULT_TAB. Tab lưu
  // qua setActiveTab mỗi lần user đổi tab.
  const restoredTabRef = useRef(false)
  useEffect(() => {
    if (restoredTabRef.current) return
    if (poPref.isLoading) return // chờ pref load xong
    restoredTabRef.current = true
    const saved = poPref.value.tab
    const target: TabKey = isTabKey(saved) ? saved : DEFAULT_TAB
    const seg3 = location.pathname.split('/').filter(Boolean)[3]
    if (seg3 !== target) {
      navigate(`/${factoryCode}/report/batch-report/${target}`, { replace: true })
    }
  }, [poPref.isLoading, poPref.value.tab, location.pathname, factoryCode, navigate])

  // Mở Advanced (cột Edit %) khi PO ở Processing; Finished → tắt. 1 lần/PO (ref
  // guard), sau đó user tự toggle. Tách khỏi rule landing đã bỏ.
  const advancedForPo = useRef('')
  useEffect(() => {
    const po = poNo.trim()
    if (!po || persistLoading) return
    if (advancedForPo.current === po) return
    advancedForPo.current = po
    setShowAdvanced(!persist?.isFinished)
  }, [poNo, persist, persistLoading])

  // Trạng thái của PO đang chọn: is_finished true = Finished (BR chốt = PO đóng),
  // false = Processing. Hiển thị + đổi ngay trên header (cùng dòng PO No.).
  const [setBatchReportStatus, { isLoading: settingStatus }] = useSetBatchReportStatusMutation()
  const isFinished = !!persist?.isFinished
  const canChangeStatus = canEdit && !!factoryCode && !!poNo.trim() && !settingStatus
  // Mỗi lần đổi Processing↔Finished phải confirm qua popup trước khi áp. Giữ
  // trạng thái đích chờ confirm; null = không có popup.
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null)
  // Click chọn option combobox → mở popup confirm (chưa áp ngay).
  const requestSetStatus = (finished: boolean) => {
    if (!canChangeStatus) return // 4-layer: select disabled + handler early-return
    if (finished === isFinished) return // không đổi → bỏ qua
    setPendingStatus(finished)
  }
  // Đổi trạng thái sang `finished`. Gọi sau khi user confirm popup. closingDate =
  // PO Closing Date (YYYY-MM-DD) khi → Finished; null khi → Processing.
  const handleSetStatus = async (finished: boolean, closingDate: string | null) => {
    if (!canChangeStatus) return // 4-layer: select disabled + handler early-return
    if (finished === isFinished) return // không đổi → bỏ qua
    if (finished) {
      // → Finished: lưu CẢ Detail + Cost (nếu đang mở BOM tab) trước khi chốt.
      // saveAll trả false khi save lỗi → KHÔNG set Finished. Không ở BOM tab
      // (bomRef null) → bỏ qua save (không có edit đang mở).
      const ok = await (bomRef.current?.saveAll() ?? Promise.resolve(true))
      if (!ok) return
    }
    let ok = true
    await setBatchReportStatus({
      factoryCode,
      poNo: poNo.trim(),
      isFinished: finished,
      poClossingDate: closingDate,
    }).unwrap().catch(() => { ok = false })
    if (!ok) return // lỗi → không reload, giữ nguyên màn
    // Đổi trạng thái thành công → reload RIÊNG vùng content (không reload cả web):
    // invalidate cache để mọi tab refetch live + bump key remount content cho
    // dựng lại sạch từ data tươi, mượt như F5 nhưng giữ nguyên app-shell/PO/tab.
    reloadData()
    // Ép refetch query persist ở page-level: chip status (header) đọc
    // `persist.isFinished`. invalidateTags chỉ refetch tab đang subscribe persist
    // (BOM/Batch Report); ở tab khác (Other info…) header KHÔNG cập nhật → gọi
    // refetch trực tiếp để chip luôn đồng bộ bất kể đang ở tab nào.
    void refetchPersist()
    // Badge trạng thái trong dropdown "Select PO…" đọc `poMeta` (query po-list).
    // Cùng lý do với chip header: ép refetch để badge không kẹt trạng thái cũ
    // tới khi reload trang (BUG-2).
    void refetchPoList()
    setStatusReloadSignal((k) => k + 1)
    // → Processing (mở khoá): bật Advanced; → Finished: tắt.
    setShowAdvanced(!finished)
  }

  // Cờ quy chuẩn (is_batchstandard): 1 = Std, 0 = Non-std. `mode` DERIVE từ persist
  // (single source, như chip Finished/Processing). Click nút → set + invalidate →
  // refetch persist → mode phản ánh.
  const [setBatchReportStandard, { isLoading: settingStd }] = useSetBatchReportStandardMutation()
  const mode: Mode = persist?.isBatchStandard === 1 ? 'qui chuan' : 'chua qui chuan'
  const canChangeStandard = canEdit && !!factoryCode && !!poNo.trim() && !settingStd
  const setStandard = async (std: boolean) => {
    if (!canChangeStandard) return // 4-layer: chip disabled + handler early-return
    if ((std ? 1 : 0) === (persist?.isBatchStandard ?? 0)) return // không đổi → bỏ qua
    let ok = true
    await setBatchReportStandard({ factoryCode, poNo: poNo.trim(), isBatchStandard: std ? 1 : 0 })
      .unwrap()
      .catch(() => { ok = false })
    if (!ok) return // lỗi → không reload, giữ nguyên màn
    // Đổi cờ quy chuẩn thành công → reload RIÊNG vùng content như đổi status:
    // invalidate + bump signal (overlay Loading + remount content), không reload web.
    reloadData()
    // `mode` (nút Std/Non-std) derive từ persist — cùng lý do với chip status:
    // ép refetch để nút đồng bộ ngay ở mọi tab, không kẹt giá trị cũ.
    void refetchPersist()
    setStatusReloadSignal((k) => k + 1)
  }

  // INPUT/OUTPUT: BatchReportTab tự query view (`v_batchreport_issue` /
  // `v_batchreport_receive`) theo PO — không truyền rows từ đây nữa.

  // Nút Advanced luôn hiện trên mọi tab (theo yêu cầu); state `showAdvanced`
  // tác động cột mở rộng của tab Batch Report + BOM (% In/Out Edit + Default).
  const showAdvancedToggle = true

  // BOM tab (label động theo BOM code) chèn NGAY TRƯỚC Standardization.
  const TABS = useMemo<TabConfig[]>(() => {
    const bom: TabConfig = { key: 'bom', label: `BOM-${selectedBomCode || 'BTA1'}` }
    const out: TabConfig[] = []
    for (const t of STATIC_TABS) {
      if (t.key === 'standardization') out.push(bom)
      out.push(t)
    }
    return out
  }, [selectedBomCode])

  // Nav dọc (left rail) ở md+; màn nhỏ (< md) → nav ngang phía trên cho gọn.
  const verticalNav = useIsDesktop()

  // Tiêu đề động theo tab đang chọn (vd chọn "Batch Report" → show "Batch
  // Report"); fallback module name khi không khớp.
  const activeTabLabel = useMemo(
    () => TABS.find((t) => t.key === activeTab)?.label ?? 'Production Report',
    [TABS, activeTab],
  )

  return (
    // Report cao tự nhiên (show HẾT nội dung, KHÔNG scroll nội bộ) → vùng
    // `<main>` của app cuộn cả trang. Title + tab nav dùng position:sticky để
    // không biến mất khi cuộn.
    <>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        // Viền nhẹ + bo góc → khung Report tách biệt rõ với phần UI ngoài.
        border: `1px solid ${COLORS.border}`,
        borderRadius: 2,
        bgcolor: 'background.paper',
        px: 0.5,
        pb: 0.5,
      }}
    >
      {/*
       * Title row — Back · Title · factory chip · subtitle stats inline ·
       * spacer · interactive controls (PO + mode + advanced).
       *
       * Stats hiển thị dạng subtitle compact ngay sau title (dot-separated
       * không có label) để KHÔNG wrap thành row riêng khi viewport hẹp —
       * vẫn là context info nhưng giờ chỉ tốn ~180px thay vì ~340px.
       */}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        useFlexGap
        flexWrap="nowrap"
        sx={{
          // pt cao hơn pb: chừa chỗ cho floating label "PO No." (notch viền nhô
          // lên) khỏi bị clip bởi overflowX:auto (ép overflow-y clip theo).
          pt: 1.25,
          pb: 0.25,
          px: 0.25,
          rowGap: 0.5,
          // Sticky: title row đứng yên trên cùng khi cuộn content.
          position: 'sticky',
          top: 0,
          zIndex: 3,
          bgcolor: 'background.paper',
          // Safety: màn cực nhỏ nếu vẫn tràn → scroll ngang để không cắt mất
          // nút (Advanced) ngoài cùng phải. Scrollbar mảnh.
          overflowX: 'auto',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 5 },
          '&::-webkit-scrollbar-thumb': { background: COLORS.borderStrong, borderRadius: 999 },
        }}
      >
        {onBackToHub ? (
          <Button
            size="small"
            onClick={() => onBackToHub?.()}
            variant="text"
            aria-label="Reports"
            title="Reports"
            sx={{
              textTransform: 'none',
              color: COLORS.textMuted,
              minWidth: 0,
              px: 0.5,
              py: 0.25,
              flexShrink: 0,
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </Button>
        ) : null}
        <Typography
          noWrap
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 700,
            color: COLORS.text,
            letterSpacing: 0.2,
            // Header nowrap: title co lại + ellipsis để combobox/nút giữ trên
            // cùng hàng mũi tên ở màn nhỏ.
            minWidth: 0,
            flexShrink: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {activeTabLabel}
        </Typography>

        {/* Spacer push controls về phải. */}
        <Box sx={{ flex: 1, minWidth: 0 }} />

        {/* Controls cluster — giữ PO + Mode + Advanced trên CÙNG 1 dòng (nowrap)
            để nút Advanced không rớt xuống dòng riêng ở màn nhỏ. */}
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ flexWrap: 'nowrap', flexShrink: 0 }}
        >
        {/* Label inline (không dùng floating label vì title row clip overflow-y
            → label nhô lên bị cắt). Ẩn ở xs cho gọn hàng nowrap. */}
        <Autocomplete
          size="small"
          // Danh sách PO bám đúng Date filter chung — KHÔNG inject PO ngoài range.
          // Selection luôn ∈ poNoOptions (đảm bảo bởi effect normalize ở trên) nên
          // value không bị blank.
          options={poNoOptions}
          value={poNo || null}
          onChange={(_e, v) => selectPo(v ?? '')}
          getOptionLabel={(o) => o || ''}
          isOptionEqualToValue={(a, b) => a === b}
          filterOptions={(opts, state) => {
            const q = state.inputValue.toLowerCase().trim()
            return q ? opts.filter((o) => o.toLowerCase().includes(q)) : opts
          }}
          // Mỗi option: PO No. (đậm) + dòng phụ BOM Code · Date PO.
          renderOption={(props, option) => {
            const m = poMeta[option]
            // React 19: `key` phải truyền trực tiếp, không spread (tách khỏi props).
            const { key, ...optionProps } = props as typeof props & { key?: string | number }
            return (
              <Box component="li" key={key} {...optionProps} sx={{ display: 'block !important', py: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: COLORS.text, lineHeight: 1.25, flex: '1 1 auto', minWidth: 0 }}>
                    {option}
                  </Typography>
                  {/* Trạng thái PO (is_finished): Finished = teal, Processing = copper. */}
                  {m ? (
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 0,
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        lineHeight: 1.4,
                        px: 0.625,
                        borderRadius: 0.75,
                        color: m.isFinished ? COLORS.success : COLORS.warn,
                        bgcolor: m.isFinished ? COLORS.successSoft : COLORS.warnSoft,
                      }}
                    >
                      {m.isFinished ? 'Finished' : 'Processing'}
                    </Box>
                  ) : null}
                </Box>
                {m && (m.bomCode || m.poDate) ? (
                  <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted, lineHeight: 1.2 }}>
                    {[m.bomCode, m.poDate].filter(Boolean).join(' · ')}
                  </Typography>
                ) : null}
              </Box>
            )
          }}
          // Dropdown rộng hơn input để chứa dòng phụ thoải mái.
          slotProps={{ paper: { sx: { minWidth: 240 } } }}
          // Style combobox theo skill `/responsive-style` (COMBOBOX_FILTER_COMPACT):
          // xs KHÔNG override font-size <input> → giữ global 16px iOS rule (no
          // auto-zoom khi tap), shrink visually bằng transform scale. Compose
          // bằng sx-array để không đụng vào input fontSize.
          sx={[
            // COMBOBOX_FILTER_COMPACT là SxProps<Theme> → ép về phần tử sx-array
            // hợp lệ (SystemStyleObject); nó là object thuần nên cast an toàn.
            COMBOBOX_FILTER_COMPACT as Exclude<SxProps<Theme>, readonly unknown[]>,
            {
              width: { xs: 168, sm: 220, md: 260 },
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderRadius: 1,
              // HỦY transform scale của COMBOBOX_FILTER_COMPACT trong hàng nowrap
              // chật: scale KHÔNG giảm layout width (vẫn chiếm full dù hiện nhỏ)
              // → đẩy nút Advanced ra ngoài & bị cắt. Dùng width thật nhỏ trên xs
              // thay vì scale (font input vẫn 16px → no iOS zoom).
              transform: 'none',
              // Giá trị PO No hiển thị in đậm.
              '& .MuiInputBase-input': { fontWeight: 700 },
              // Viền rõ + đổi màu primary khi hover/focus → dễ nhận diện & focus.
              '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.borderStrong },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.primary },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.primary,
                borderWidth: 2,
              },
            },
          ]}
          renderInput={(params) => (
            <TextField
              {...params}
              label="PO No."
              placeholder="Select PO…"
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              // Mobile: readOnly <input> → tap KHÔNG bung keyboard (chỉ pick từ
              // dropdown). Desktop giữ search gõ tay. (skill /responsive Pattern 14)
              inputProps={{ ...params.inputProps, readOnly: isMobile }}
            />
          )}
        />

        {/* Trạng thái của PO đang chọn (is_finished): 1 combobox
            gộp chip + nút đổi — cùng dòng PO No. 1 = Finished, 0 = Processing.
            Chọn option = trigger event y như nút Mark as Finished/Processing cũ. */}
            <Select
              size="small"
              value={isFinished ? 'finished' : 'processing'}
              disabled={!canChangeStatus}
              onChange={(e) => requestSetStatus(e.target.value === 'finished')}
              startAdornment={
                settingStatus ? <CircularProgress size={13} sx={{ mr: 0.5 }} color="inherit" /> : undefined
              }
              sx={{
                flexShrink: 0,
                fontSize: '0.72rem',
                fontWeight: 700,
                minHeight: 28,
                color: isFinished ? COLORS.success : COLORS.warn,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isFinished ? COLORS.success : COLORS.warn,
                },
                '& .MuiSelect-select': { py: 0.25 },
              }}
            >
              <MenuItem value="processing" sx={{ fontSize: '0.78rem', fontWeight: 700, color: COLORS.warn }}>
                Processing
              </MenuItem>
              <MenuItem value="finished" sx={{ fontSize: '0.78rem', fontWeight: 700, color: COLORS.success }}>
                Finished
              </MenuItem>
            </Select>

        {/* Mode toggle — 1 combobox gộp 2 nút Non-std/Std. lưu po.is_batchstandard
            (Non-std=0, Std=1). Chọn option = trigger event y như nút cũ. */}
            <Select
              size="small"
              value={mode === 'qui chuan' ? 'std' : 'nonstd'}
              disabled={!canChangeStandard}
              onChange={(e) => setStandard(e.target.value === 'std')}
              startAdornment={
                settingStd ? <CircularProgress size={13} sx={{ mr: 0.5 }} color="inherit" /> : undefined
              }
              sx={{
                flexShrink: 0,
                fontSize: '0.72rem',
                fontWeight: 700,
                minHeight: 28,
                '& .MuiSelect-select': { py: 0.25 },
              }}
            >
              <MenuItem value="nonstd" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                Non-std
              </MenuItem>
              <MenuItem value="std" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                Std
              </MenuItem>
            </Select>

        <Tooltip title={!poNo.trim() ? 'Select a PO first' : 'Export PO Report to Excel'}>
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={handleExport}
              disabled={exporting || !poNo.trim()}
              startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <FileDownloadIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem' }}
            >
              Export
            </Button>
          </span>
        </Tooltip>

        {showAdvancedToggle ? (
          <FormControlLabel
            control={
              <Switch size="small" checked={showAdvanced} onChange={(_, v) => setShowAdvanced(v)} />
            }
            label={
              <Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted, ...HIDE_ON_MOBILE_INLINE }}>
                Advanced
              </Typography>
            }
            sx={{ ml: 0, mr: 0 }}
          />
        ) : null}
        </Stack>
      </Stack>

      {/*
       * Body — Level-2 nav DỌC (left rail) + content (right).
       *
       * Differentiation từ level-1 (top factory tabs, underline style):
       *   - Pill rounded background thay vì underline; active = solid primary
       *     bg + white text.
       *   - Rail wrapper bg `bgSubtle` + border → signal "section nội bộ".
       *
       * Rail dọc cố định bên trái (full-width pills, text trái), content chiếm
       * phần còn lại; mỗi bên scroll độc lập.
       */}
      <Box
        sx={{
          mt: 0.25,
          display: 'flex',
          flexDirection: verticalNav ? 'row' : 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          gap: 0.75,
        }}
      >
        {/* Tab nav — dọc (rail trái) ở md+, ngang (bar trên) ở màn nhỏ.
            Cố định trong khung (sibling của content) → ngang hàng với content. */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            gap: 0.375,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar-thumb': { background: COLORS.borderStrong, borderRadius: 999 },
            ...(verticalNav
              ? {
                  width: { md: 100, lg: 108 },
                  alignSelf: 'flex-start',
                  flexDirection: 'column',
                  // Vạch phân cách mảnh bên phải thay cho khung viền bo tròn.
                  borderRight: `1px solid ${COLORS.border}`,
                  pr: 0.75,
                }
              : {
                  flexDirection: 'row',
                  overflowX: 'auto',
                  pb: 0.5,
                  borderBottom: `1px solid ${COLORS.border}`,
                  '&::-webkit-scrollbar': { height: 6 },
                }),
          }}
        >
          {TABS.map((t) => {
            const active = t.key === activeTab
            return (
              <Button
                key={t.key}
                size="small"
                disableElevation
                fullWidth={verticalNav}
                onClick={() => setActiveTab(t.key)}
                sx={{
                  textTransform: 'none',
                  py: 0.5,
                  minHeight: 28,
                  lineHeight: 1.2,
                  fontSize: verticalNav ? '0.72rem' : '0.78rem',
                  fontWeight: active ? 800 : 600,
                  letterSpacing: active ? 0.3 : 0.1,
                  bgcolor: active ? COLORS.primary : 'transparent',
                  color: active ? '#fff' : COLORS.textMuted,
                  // Active: glow shadow để pill nổi bật khỏi rail.
                  boxShadow: active ? '0 2px 8px rgba(20,90,89,0.32)' : 'none',
                  transition: 'background-color 120ms ease, color 120ms ease, box-shadow 120ms ease',
                  ...(verticalNav
                    ? {
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        whiteSpace: 'normal',
                        borderRadius: 1,
                        position: 'relative',
                        // Chừa chỗ cho thanh accent trái khi active.
                        pl: active ? 1.25 : 0.875,
                        pr: 0.875,
                        // Thanh accent trắng bên trái → nhấn mạnh item đang chọn.
                        '&::before': active
                          ? {
                              content: '""',
                              position: 'absolute',
                              left: 4,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: 3,
                              height: '58%',
                              borderRadius: 999,
                              backgroundColor: '#fff',
                              opacity: 0.95,
                            }
                          : undefined,
                      }
                    : {
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        minWidth: 0,
                        borderRadius: 999,
                        px: 1.5,
                      }),
                  '&:hover': {
                    bgcolor: active ? COLORS.primary : COLORS.primarySoft,
                    color: active ? '#fff' : COLORS.primary,
                  },
                }}
              >
                {t.label}
              </Button>
            )
          })}
        </Box>

        {/* Tab content — vùng scroll nội bộ (Detail report dài → cuộn được;
            content vừa khung → không hiện scrollbar). */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'auto',
            position: 'relative',
            pt: verticalNav ? 0.25 : 0.75,
            // Chừa khoảng cách phải để content không sát thanh scroll.
            pr: 1,
            // Bảng trong report: SHOW HẾT hàng, KHÔNG scroll dọc riêng — cuộn dọc
            // do vùng content này đảm nhận. Bỏ cap `max-height:60vh` của
            // `.app-dataframe--compact` + để table tự cao theo content.
            '& .app-dataframe, & .app-dataframe--compact': {
              height: 'auto',
              maxHeight: 'none',
              overflow: 'visible',
            },
            '& .app-table-wrap': {
              height: 'auto',
              maxHeight: 'none',
              overflowX: 'auto',
              overflowY: 'visible',
            },
            '& .app-table-wrap .MuiTable-root': { minHeight: 0 },
          }}
        >
          {/* Overlay Loading khi đổi PO / đổi tab — phủ content tới khi API tab
              active trả về (sticky để luôn nằm giữa viewport dù content cao). */}
          {showContentLoading ? (
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 5,
                height: '100%',
                minHeight: 240,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                bgcolor: 'rgba(255,255,255,0.78)',
              }}
            >
              <CircularProgress size={28} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: COLORS.textMuted }}>
                Loading…
              </Typography>
            </Box>
          ) : null}
          <Box key={`${contentKey}|${statusReloadSignal}`} sx={{ display: showContentLoading ? 'none' : 'block' }}>
          {activeTab === 'dashboard' ? (
            <DashboardTab factoryCode={factoryCode} poNo={poNo} poType={selectedBomCode || '—'} />
          ) : activeTab === 'batch-report' ? (
            <BatchReportTab showAdvanced={showAdvanced} factoryCode={factoryCode} poNo={poNo} inputValue={batchInputValue} />
          ) : activeTab === 'production-analysis' ? (
            <ProductionAnalysisTab factoryCode={factoryCode} poNo={poNo} bomCode={selectedBomCode} poDateISO={selectedPoDateISO} mode={mode} canEdit={canEdit} />
          ) : activeTab === 'standardization' ? (
            <StandardizationTab factoryCode={factoryCode} poNo={poNo} canEdit={canEdit} />
          ) : activeTab === 'comparison' ? (
            <ComparisonTab factoryCode={factoryCode} poNo={poNo} poOptions={poNoOptions} poMeta={poMeta} cmpPo={comparisonPo} onCmpPoChange={handleComparisonPoChange} />
          ) : activeTab === 'item' ? (
            <ItemTab ref={itemRef} factoryCode={factoryCode} poNo={poNo} canEdit={canEdit} poOptions={poNoOptions} poMeta={poMeta} onDirtyChange={setItemDirty} />
          ) : (
            <BomTab ref={bomRef} bomCode={selectedBomCode} factoryCode={factoryCode} poNo={poNo} poDate={selectedPoDate} poDateISO={selectedPoDateISO} canEdit={canEdit} showAdvanced={showAdvanced} onDirtyChange={setBomDirty} />
          )}
          </Box>
        </Box>
      </Box>
    </Box>

      {/* Unsaved-changes confirm — đổi URL rời tab `bom` (useBlocker) / đổi PO khi dirty. */}
      <Dialog open={guardOpen} onClose={() => !savingNav && cancelLeave()} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            You have unsaved changes. Save them before leaving?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancelLeave} disabled={savingNav} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button color="inherit" onClick={resolveLeave} disabled={savingNav} sx={{ textTransform: 'none' }}>
            Discard
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={savingNav}
            startIcon={savingNav ? <CircularProgress size={14} color="inherit" /> : undefined}
            onClick={saveThenLeave}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {savingNav ? 'Saving…' : 'Save & continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm đổi trạng thái Processing ↔ Finished — dùng chung PoFinishedDialog
          với PO List (→ Finished hiện PO Closing Date default hôm nay; → Processing
          null closing date). Handler/reload riêng của Batch Report giữ nguyên. */}
      <PoFinishedDialog
        open={pendingStatus !== null}
        toFinished={!!pendingStatus}
        poNo={poNo.trim()}
        saving={settingStatus}
        onClose={() => !settingStatus && setPendingStatus(null)}
        onConfirm={(closingDate) => {
          const target = pendingStatus
          setPendingStatus(null)
          if (target !== null) void handleSetStatus(target, closingDate)
        }}
      />

      <Snackbar
        open={!!exportError}
        autoHideDuration={5000}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setExportError(null)} sx={{ width: '100%' }}>
          {exportError}
        </Alert>
      </Snackbar>
    </>
  )
}

/* ---------- helpers ------------------------------------------------------ */

